import { sendPromptToLLM, extractContentFromResponse } from './openrouter-api';
import { getAgeGroupSettings } from '../utils/age-groups';

// Preferred model for general story generation
const STORY_GENERATION_MODEL = 'anthropic/claude-3.5-sonnet';

/**
 * Generates a fairy tale based on user inputs
 * @param {string} ageGroup - The selected age group ID
 * @param {string} character - The main character description
 * @param {string} setting - The story setting description
 * @returns {Promise<Object>} - The generated story object
 */
export async function generateStory(ageGroup, character, setting) {
  try {
    const ageSettings = getAgeGroupSettings(ageGroup);
    
    // Create the prompt based on age group
    const prompt = createStoryPrompt(ageSettings, character, setting);
    
    // Send to LLM
    const response = await sendPromptToLLM(prompt, STORY_GENERATION_MODEL);
    
    // Extract and parse the story content
    const storyData = extractContentFromResponse(response);
    
    // Format and validate the story structure
    return formatStoryResponse(storyData, ageGroup, character, setting);
  } catch (error) {
    console.error('[story-generator] Error generating story:', error);
    throw error;
  }
}

/**
 * Creates the specialized prompt for story generation
 * @param {Object} ageSettings - Settings for the target age group
 * @param {string} character - The main character description
 * @param {string} setting - The story setting description
 * @returns {string} - The formatted prompt
 */
function createStoryPrompt(ageSettings, character, setting) {
  return `You are "Fairy Tale Creator," an AI specialized in crafting enchanting, imaginative, and age-appropriate short fairy tales tailored for children.

Your task is to create a whimsical fairy tale for children in the ${ageSettings.name} age group (${ageSettings.ageRange}).

The main character is: ${character}
The setting is: ${setting}

Please follow these guidelines based on the age group:
- Vocabulary level: ${ageSettings.vocabularyLevel}
- Sentence complexity: ${ageSettings.sentenceComplexity}
- Story length: ${ageSettings.storyLength}
- Themes: ${ageSettings.appropriateThemes}

Your response MUST be in the following JSON format:
{
  "title": "The title of the fairy tale",
  "moral": "The moral or lesson of the story",
  "scenes": [
    {
      "text": "Text for the first scene",
      "imageKeywords": "Keywords for image generation for this scene" 
    },
    {
      "text": "Text for the second scene",
      "imageKeywords": "Keywords for image generation for this scene"
    },
    ...more scenes...
  ]
}

The scenes should flow naturally and tell a cohesive story. The imageKeywords should be descriptive enough to generate an image that matches the scene.

For ${ageSettings.name}, ensure the story is ${ageSettings.specialInstructions}.

IMPORTANT: 
1. The story MUST be age-appropriate and positive.
2. Your response MUST be valid JSON that can be parsed.
3. Include 3-6 scenes total, appropriate for the attention span of this age group.
4. Make image keywords clear, descriptive and child-appropriate.`;
}

/**
 * Formats and validates the story response from the LLM
 * @param {Object|string} response - The raw response from the LLM
 * @param {string} ageGroup - The age group ID
 * @param {string} character - The character description
 * @param {string} setting - The setting description
 * @returns {Object} - The formatted story object
 */
function formatStoryResponse(response, ageGroup, character, setting) {
  // If response is a string, try to parse it as JSON again
  let storyData = response;
  if (typeof response === 'string') {
    try {
      storyData = JSON.parse(response);
    } catch (e) {
      console.error('[story-generator] Failed to parse story response as JSON');
      throw new Error('The story generator returned an invalid response format');
    }
  }
  
  // Validate the story has the required structure
  if (!storyData.title || !storyData.scenes || !Array.isArray(storyData.scenes)) {
    throw new Error('Generated story is missing required elements');
  }
  
  // Add metadata
  return {
    ...storyData,
    ageGroup,
    character,
    setting,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Regenerates a specific scene in an existing story
 * @param {Object} story - The existing story object
 * @param {number} sceneIndex - The index of the scene to regenerate
 * @returns {Promise<Object>} - The updated story object
 */
export async function regenerateScene(story, sceneIndex) {
  if (!story || !story.scenes || sceneIndex >= story.scenes.length) {
    throw new Error('Invalid story or scene index');
  }
  
  try {
    const ageSettings = getAgeGroupSettings(story.ageGroup);
    
    // Create a prompt specific to regenerating this one scene
    const prompt = `You are "Fairy Tale Creator." Please rewrite the following scene from a fairy tale about ${story.character} in ${story.setting}. 
    
The story is titled "${story.title}" and has a moral about "${story.moral}".

Original scene: "${story.scenes[sceneIndex].text}"

Please rewrite this scene to make it more engaging while keeping the same basic events and appropriate for ${ageSettings.name} (${ageSettings.ageRange}).

Respond with ONLY a JSON object in this format:
{
  "text": "The new scene text",
  "imageKeywords": "New keywords for image generation"
}`;
    
    // Send to LLM
    const response = await sendPromptToLLM(prompt, STORY_GENERATION_MODEL);
    
    // Extract and parse the scene content
    const newSceneData = extractContentFromResponse(response);
    
    // Update the story with the new scene
    const updatedStory = {
      ...story,
      scenes: [
        ...story.scenes.slice(0, sceneIndex),
        typeof newSceneData === 'string' 
          ? { text: newSceneData, imageKeywords: story.scenes[sceneIndex].imageKeywords }
          : newSceneData,
        ...story.scenes.slice(sceneIndex + 1)
      ]
    };
    
    return updatedStory;
  } catch (error) {
    console.error('[story-generator] Error regenerating scene:', error);
    throw error;
  }
}
